import { getComputedStyle } from '../../../helpers/dom/element';
import { maybeFixCellBorder } from '../../../helpers/dom/table';
import { DEFAULT_BORDER_COLOR } from './decorateTable';

export function preProcessPastedTables(htmlToPaste: string, document: Document) {
  const container = document.createElement('template');
  container.innerHTML = htmlToPaste;
  const { content } = container;

  const tables = content.querySelectorAll('table');
  // If more than one table do nothing.
  if (tables.length !== 1) {
    return htmlToPaste;
  }
  const [table] = tables;
  // If more than one root element that is not META or STYLE, do nothing.
  const notMetaNotStyleRootElements = Array.from(content.children).filter(
    e => !['META', 'STYLE', 'BR'].includes(e.tagName),
  );
  if (notMetaNotStyleRootElements.length !== 1) {
    return htmlToPaste;
  }
  const [element] = notMetaNotStyleRootElements;
  // Process table only if it's element, of if it's its only descendant (which is the case if table is only wrapped in several nested wrappers.
  if (isOnlyRelevantDescendent(element, table)) {
    const isExcelTable = getIsExcelTable(table);
    maybeSetDefaultTableStyle(table, isExcelTable);
    maybeUnwrapTableFromPre(table, content);
    removeTableFixedWidth(table);
    return container.innerHTML;
  }
  return htmlToPaste;
}

/**
 * Check if descendant is the only descendant of root that is not a wrapper around descendant or a BR.
 */
function isOnlyRelevantDescendent(root: Element, descendant: Element) {
  // True if both elements are equal.
  if (descendant === root) {
    return true;
  }
  let { parentElement } = descendant;
  while (parentElement !== root) {
    // If parentElement is null, it means we reached the end of the fragment, without hitting root,
    // so descendant is not its descendant.
    if (parentElement === null) {
      return false;
    }
    // Check that element has only one child that is not a BR.
    const relevantChildrenCount = Array.from(parentElement.children).filter(c => c.tagName !== 'BR').length;
    if (relevantChildrenCount !== 1) {
      return false;
    }
    parentElement = parentElement.parentElement;
  }
  return true;
}

/**
 * Unwrap table form PRE.
 */
function maybeUnwrapTableFromPre(table: HTMLTableElement, documentFragment: DocumentFragment) {
  const { parentElement: wrapper } = table;
  if (!wrapper || !['PRE'].includes(wrapper.tagName)) {
    return;
  }
  const wrapperParent = wrapper.parentElement || documentFragment;
  // Unwrap table.
  wrapperParent.insertBefore(table, wrapper);
  if (wrapper.children.length === 0) {
    wrapper.remove();
  }
}

/*
 * This is mostly a workaround Google Stylesheet's table inline style.
 * It specifies a 0px width, which is compensated with a colgroup specifying a width for each col.
 * Unfortunately the colgroup does not get updated when adding columns, so added column are 0px wide.
 */
/**
 * Remove table's width limit so that added columns get space.
 */
function removeTableFixedWidth(table: HTMLTableElement) {
  const { style } = table;
  if (!style) {
    return;
  }
  const { width, maxWidth } = style;
  if (width !== null && width.length > 0) {
    table.style.setProperty('width', 'auto');
  }
  if (maxWidth !== null && maxWidth.length > 0) {
    table.style.setProperty('maxWidth', 'auto');
  }
}

/**
 * Provides the tables we'll manipulate a light default style.
 */
function maybeSetDefaultTableStyle(table: HTMLTableElement, isExcelTable: boolean) {
  const styleAttr = table.getAttribute('style');
  const { style } = table;
  const cells = Array.from(table.querySelectorAll('td, th')) as HTMLTableCellElement[];

  // We always want to have border-collapse: collapse.
  if (!styleAttr) {
    table.setAttribute('style', 'border-collapse: collapse;');
  } else if (style.borderCollapse !== 'collapse') {
    style.setProperty('border-collapse', 'collapse');
  }

  const allCellsWithoutStyle =
    cells.length === [...cells].filter(cell => cell.getAttribute('style') === null).length;
  // If no style at all (not on table nor on cells), provide arbitrary style to cells
  if (!styleAttr && allCellsWithoutStyle && isExcelTable) {
    cells.forEach(cell =>
      cell.setAttribute('style', `border: 1px solid ${DEFAULT_BORDER_COLOR}; width: 70px;`),
    );
  }
  // Otherwise just check each cell's border.
  else {
    cells.forEach(cell => maybeFixCellBorder(cell, getComputedStyle(cell)));
  }
}

function getIsExcelTable(table: HTMLTableElement) {
  return (
    table.getAttribute('style') === null &&
    table.parentElement !== null &&
    table.parentElement.tagName === 'PRE'
  );
}
